/**
 * One entry per workspace data-plane Lambda the UI talks to, each pointing at
 * that service's `_info` route.
 *
 * `_info` is the cheapest handler every one of these services has: it returns
 * static infra metadata, and on the two exporters it is explicitly excluded
 * from the lazy sub-app mount, so the request pays container init and nothing
 * else. Warming the execution environment is the point; the response is
 * discarded.
 *
 * Not every data-plane Lambda can be reached this way. The analytics query
 * Lambda and llm-inference expose no GET route through the workspace gateway,
 * and case management and features control have no `_info` handler, so they
 * stay cold until their own first request.
 */
export type DataplanePrewarmTarget = {
  /** Stable id, used as the dedup key together with the workspace id. */
  id: string;
  /** Path under the workspace base URL. */
  path: string;
};

export const DATAPLANE_PREWARM_TARGETS: readonly DataplanePrewarmTarget[] = [
  { id: "decision-engine", path: "/run/api/v1/_info" },
  // Also serves datasets, dataset jobs, jobs, review, s3 bucket datasets and
  // entities.
  { id: "base-exporter", path: "/datasets/api/v1/_info" },
  // Also serves metrics, outcomes, simulations and history tasks.
  { id: "history-exporter", path: "/history/api/v1/_info" },
  { id: "connect", path: "/connect/api/v1/_info" },
  { id: "files", path: "/public/files/api/v1/_info" },
  { id: "analytics-dashboard", path: "/analytics/api/_info" },
  { id: "agent-management", path: "/agents/management/api/v0/_info" },
] as const;
